import { cn } from '@/utils/cn';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useMemo } from 'react';

type PaginationProps = {
  maxVisiblePages?: number;
  limit: number;
  page: number;
  count: number;
  onPageChange?: (newPage: number) => void;
};

const Pagination = ({ limit, page, count, onPageChange, maxVisiblePages = 7 }: PaginationProps) => {
  const totalPages = useMemo(() => Math.ceil(count / limit), [count, limit]);

  const handlePrev = useCallback(() => {
    if (page > 1) onPageChange?.(page - 1);
  }, [onPageChange, page]);

  const handleNext = useCallback(() => {
    if (page < totalPages) onPageChange?.(page + 1);
  }, [onPageChange, page, totalPages]);

  // Generate page numbers with ellipsis logic
  const getPageNumbers = useCallback(() => {
    const pages: (number | string)[] = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than or equal to max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Calculate how many pages to show around current page
      const halfVisible = Math.floor(maxVisiblePages / 2);
      const startPage = Math.max(1, page - halfVisible);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      // Adjust start page if we're near the end
      const adjustedStartPage = Math.max(1, endPage - maxVisiblePages + 1);

      // Always show first page
      pages.push(1);

      if (adjustedStartPage > 2) {
        pages.push('...');
      }

      // Show pages around current page
      for (let i = adjustedStartPage; i <= endPage; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (endPage < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page (if not already shown)
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }, [totalPages, page, maxVisiblePages]);

  return (
    <div className="flex space-x-[4px]">
      <button
        className={cn(
          'border w-[27px] cursor-pointer h-[26px] flex items-center justify-center border-[#D9D9D9] rounded-[5px] disabled:opacity-50',
          {
            'cursor-not-allowed': page <= 1,
          }
        )}
        onClick={(e) => {
          e.preventDefault();
          handlePrev();
        }}
        disabled={page <= 1}
        aria-label="Go to previous page"
        title="Previous page"
      >
        <ChevronLeft className="w-[10px] h-[10px] text-[#9D9D9C]" />
      </button>

      {getPageNumbers().map((pageItem, idx) => {
        if (pageItem === '...') {
          return (
            <span
              key={`ellipsis-${idx}`}
              className="border w-[27px] h-[26px] flex items-center justify-center border-[#D9D9D9] rounded-[5px] text-[10px] text-[#9D9D9C]"
            >
              ...
            </span>
          );
        }

        const pageNumber = pageItem as number;
        const isActive = pageNumber === page;
        return (
          <button
            key={pageNumber}
            onClick={(e) => {
              e.preventDefault();
              onPageChange?.(pageNumber);
            }}
            className={cn(
              'border w-[27px] cursor-pointer h-[26px] flex items-center justify-center border-[#D9D9D9] rounded-[5px] disabled:opacity-50 text-[10px]',
              {
                'bg-[#0C8DBF] border-[#0C8DBF] text-[#FFFFFF]': isActive,
              }
            )}
            aria-label={`Go to page ${pageNumber}`}
            aria-current={isActive ? 'page' : undefined}
            title={`Page ${pageNumber}`}
          >
            {pageNumber}
          </button>
        );
      })}

      <button
        className={cn(
          'border w-[27px] cursor-pointer h-[26px] flex items-center justify-center border-[#D9D9D9] rounded-[5px] disabled:opacity-50',
          {
            'cursor-not-allowed': page >= totalPages,
          }
        )}
        onClick={(e) => {
          e.preventDefault();
          handleNext();
        }}
        disabled={page >= totalPages}
        aria-label="Go to next page"
        title="Next page"
      >
        <ChevronRight className="w-[10px] h-[10px] text-[#9D9D9C]" />
      </button>
    </div>
  );
};

export default Pagination;
